import React, { ChangeEvent, useCallback, useEffect, useRef } from 'react'
import Slider from 'react-slick'
import './index.scss'
import PodcastsCard from 'cwp-components/cards/PodcastCard'
import SpeakerList from './components/SpeakerList'
import {
  BsFillCaretLeftFill,
  BsFillCaretRightFill,
  BsSearch,
} from 'react-icons/bs'
import { background } from 'assets/backgrounds'
import {
  usePodcastsTemplate,
  SORT_OPTIONS,
} from 'common/hooks/usePodcastTemplate'
import { Newsletter } from 'cwp-components/Newsletter'
import { InputGroupField, SelectField } from 'cwp-components/forms'
import { useNavigate } from 'react-router-dom'

const pageSize = 6

const Podcasts = () => {
  const sliderRef = useRef<any>(null)
  const navigate = useNavigate()
  const {
    results,
    sortOption,
    setSortOption,
    searchValue,
    updateSearchValue,
    loadMore,
    hasNext,
    topSpeakers,
    loadPodcasts,
    loadSpeakers,
    getPodcastMetrics,
    podcastMetrics,
  } = usePodcastsTemplate({ pageSize })

  const onSortDropdownSelect = (label: string) => {
    setSortOption(SORT_OPTIONS.find((o) => o.label === label))
  }
  useEffect(loadPodcasts, [loadPodcasts])
  useEffect(loadSpeakers, [loadSpeakers])
  useEffect(getPodcastMetrics, [getPodcastMetrics])

  const handleSearchInputChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      updateSearchValue(evt.target.value)
    },
    [],
  )

  const next = () => {
    sliderRef.current.slickNext()
  }
  const previous = () => {
    sliderRef.current.slickPrev()
  }

  const settings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: topSpeakers.length > 4 ? 4 : topSpeakers.length,
    slidesToScroll: 1,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
          infinite: true,
          dots: true,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
        },
      },
    ],
  }

  return (
    <section id='podcasts' className='relative'>
      <div className='grid md:grid-cols-[auto_500px] md:p-[180px_196.414px_239.878px_197px] p-[140px_42px_126px_41px] bg-[#9ABDEC] md:gap-[240px]'>
        <div className=''>
          <h1 className='md:text-[64px] text-[24px] max-sm:text-center font-bold text-white  '>
            Our conversations on legal life events
          </h1>
          <p className='md:mt-[58px] max-sm:my-[20px] max-sm:text-center text-white md:text-[18px] text-[16px] md:font-bold '>
            Unlock a wealth of legal knowldge by diving into our exclusive
            podcast insights.
          </p>
          <div className='flex items-center md:mt-[58px] md:gap-[20px]'>
            {[
              {
                title: podcastMetrics.podcastCount,
                description: 'podcasts',
              },
              {
                title: podcastMetrics.guestCount,
                description: 'podcast guests',
              },
              {
                title: podcastMetrics.areasCount,
                description: 'legal topics',
              },
            ].map((item) => (
              <>
                <div className=''>
                  <h4 className='md:text-[32px] font-bold text-white'>
                    {item.title}
                  </h4>
                  <p className='text-white md:text-[18px] text-[12px] whitespace-nowrap'>
                    {item.description}
                  </p>
                </div>
                <hr className='!w-[70px] bg-radial !opacity-[0.2px] !h-[2px] !transform rotate-[-90deg] ' />
              </>
            ))}
          </div>
        </div>
        <div className=''>
          <img
            className='md:mt-[-100px] max-sm:mt-[40px]'
            src={background.Podcast.Hero}
            alt=''
          />
        </div>
      </div>

      <div className='latest-podcasts'>
        <h1 className='text-[18px] md:text-[40px] text-[#020251] font-bold text-center '>
          Latest podcast episodes
        </h1>
        <div className='my-[80px] flex flex-wrap md:justify-center gap-[20px]'>
          <InputGroupField
            onChange={handleSearchInputChange}
            placeholder='Type what you are searching for...'
            value={searchValue}
            className='md:w-[600px] w-full'
            Icon={BsSearch}
            reverse
          />
          <SelectField
            records={SORT_OPTIONS}
            name='sort'
            onSelectionChange={onSortDropdownSelect}
            placeholder='Filter by'
            defaultValue={sortOption}
            className={'md:w-[250px] w-[200px] '}
          />
          <button className='text-text-muted'>Clear filters</button>
        </div>
        <div className='grid auto-rows-fr grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-[26px]'>
          {results?.slice(0, 6)?.map((episode, idx) => (
            <PodcastsCard key={idx} episode={episode} />
          ))}
        </div>

        {!!hasNext && (
          <div className='flex justify-end mt-[20px]'>
            <button
              onClick={loadMore}
              className='text-underline btn bg-[#020251]'
            >
              See more
            </button>
          </div>
        )}
      </div>

      {topSpeakers && (
        <div className='speakers'>
          <div className='layer list'>
            <Slider
              ref={(slider) => {
                sliderRef.current = slider
              }}
              className='item'
              {...settings}
            >
              {topSpeakers &&
                topSpeakers.map((speaker, idx) => (
                  <>
                    <SpeakerList data={speaker} key={idx} />
                  </>
                ))}
            </Slider>
            {topSpeakers.length > 4 && (
              <div className='controls'>
                <BsFillCaretLeftFill
                  size={25}
                  className='prev'
                  onClick={previous}
                />
                <BsFillCaretRightFill
                  size={25}
                  className='next'
                  onClick={next}
                />
              </div>
            )}
          </div>
          <div className='layer'>
            <h1>Our guests</h1>
            <hr />
            <p>
              Become a guest on the Discover Law podcast to share knowledge,
              insight, and experience on legal life events.
            </p>
          </div>
          <div className='layer'>
            <h4>Would you like to join the conversation?</h4>
            <p className='mt-5'>
              Apply to be a guest on the Discover Law podcast at Legal Utopia.
            </p>
            <button
              className='btn mt-5'
              onClick={() => navigate('/contact-us')}
            >
              Contact Us
            </button>
          </div>
        </div>
      )}
      <Newsletter />
    </section>
  )
}

export default Podcasts
